import React, { useRef } from 'react';
import { SkinProps } from '../../../../types/component-skin';
import { Row, Col } from 'react-bootstrap';
import { PriceDisplay, DiscountRatePriceDisplay } from '../../../common/PriceDisplay';
import SafeImage from '../../../common/SafeImage';
import '../ProductList.css';

// CSS Position 타입 정의
type CSSPosition = 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';

// 확장된 CSS 스타일 타입
interface ExtendedCSSProperties extends React.CSSProperties {
  position?: CSSPosition;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

/**
 * ProductList 완전한 기본 스킨 - 원본 ProductList.component.tsx를 정확히 재현
 */
const BasicProductListSkinComplete: React.FC<SkinProps> = ({ data, actions, utils, app }) => {
  const { t, navigate } = utils;
  

  // ComponentSkinWrapper에서 logicData가 병합되어 data prop으로 전달됨
  // ProductListLogic의 data 객체 내용이 직접 data prop에 포함됨
  const productData = data;
  
  // Redux에서 온 products 데이터
  const products = productData.products || [];
  const loading = productData.loading || false;
  const currentPage = productData.currentPage || 1;
  const totalPages = productData.totalPages || 1;
  const isUserLoggedIn = app?.isUserLoggedIn || false;
  
  // componentProps에서 설정값 추출
  const componentProps = data.componentProps || {};
  const {
    itemsPerRow = 4,
    excludeIndexes = [],
    showStock = false,
    className = 'padding-tb-40',
    lg = 12,
    order = '',
    showRewardPoints = false,
    rewardPointBase = 'pv',
    rewardPointPercentage = 1,
    rewardPointLabel = '적립금',
    rewardPointFormat = 'amount'
  } = componentProps;
  
  // style에서 값 추출
  const id = data.id;
  
  // 모바일 관련 데이터
  const isMobile = productData.isMobile || false;
  const mobileProducts = productData.mobileProducts || [];
  const mobilePage = productData.mobilePage || 1;
  const isLoadingMore = productData.isLoadingMore || false;
  const fallbackRef = useRef<HTMLButtonElement | null>(null);
  const loadMoreButtonRef = productData.loadMoreButtonRef || fallbackRef;
  
  // UI 상태
  const isGridView = false;
  
  // xlValue 계산
  const calculateXlValue = (items: number | string): number => {
    const itemCount = Number(items);
    switch(itemCount) {
      case 2: return 6;
      case 3: return 4;
      case 6: return 2;
      default: return 3; // 기본값 4개 (xl=3)
    }
  };
  const xlValue = calculateXlValue(itemsPerRow);
  
  // 스타일 정의
  const productImageStyle = {
    imgContainer: {
      width: '100%',
      position: 'relative' as CSSPosition,
      paddingTop: '100%', // 1:1 비율 유지
      overflow: 'hidden',
      backgroundColor: '#f8f8f8',
      borderRadius: '0',
    } as ExtendedCSSProperties,
    imgWrapper: {
      position: 'absolute' as CSSPosition,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    } as ExtendedCSSProperties,
    imageLink: {
      width: "100%",
      height: "100%",
      display: "block",
      position: "relative" as CSSPosition,
    } as ExtendedCSSProperties,
    mainImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover" as 'cover',
      position: "absolute" as CSSPosition,
      top: 0,
      left: 0,
      transition: "opacity 0.3s ease",
    } as ExtendedCSSProperties,
    hoverImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover" as 'cover',
      position: "absolute" as CSSPosition,
      top: 0,
      left: 0,
      opacity: 0,
      transition: "opacity 0.3s ease",
    } as ExtendedCSSProperties,
  };
  
  // CSS 클래스 헬퍼
  const getItemsPerRowClass = () => {
    switch(Number(itemsPerRow)) {
      case 2: return 'items-per-row-2';
      case 3: return 'items-per-row-3'; 
      case 6: return 'items-per-row-6';
      default: return 'items-per-row-4';
    }
  };
  
  // 가격 포맷팅 함수
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price) + '원';
  };
  
  // 적립금 계산 함수
  const calculateRewardPoints = (product: any) => {
    if (!showRewardPoints) return null;
    
    // PV 기준일 때 PV 값이 없으면 표시하지 않음
    if (rewardPointBase === 'pv' && (!product.pv || product.pv === 0)) {
      return null;
    }
    
    // variant가 있고 추가 가격이 있는 경우 합산된 금액 사용
    let priceToUse = product.hasLevelPrice ? product.price : (product.newPrice || product.price);
    
    // variant 추가 가격이 있으면 합산 (상품 목록에서는 variant가 선택되지 않았으므로 기본 가격만 사용)
    // 주의: 상품 목록에서는 variant가 선택되지 않은 상태이므로 기본 가격만 표시
    // variant가 있는 상품의 경우 "~원부터" 같은 표시를 고려할 수 있음
    
    const baseValue = rewardPointBase === 'pv' ? product.pv : priceToUse;
    const points = Math.floor(baseValue * (rewardPointPercentage / 100));
    
    if (points === 0) return null;
    
    // variant가 있는 상품인 경우 "~부터" 표시 추가
    const prefix = product.hasOptions ? '~' : '';
    
    switch (rewardPointFormat) {
      case 'percentage':
        return `${prefix}${rewardPointPercentage}%`;
      case 'both':
        return `${prefix}${formatPrice(points)} (${rewardPointPercentage}%)`;
      case 'amount':
      default:
        return `${prefix}${formatPrice(points)}`;
    }
  };
  
  // 로컬 함수들
  const handleImageHover = (e: React.MouseEvent, isHover: boolean) => {
    const mainImage = e.currentTarget.querySelector('.main-image') as HTMLElement;
    const hoverImage = e.currentTarget.querySelector('.hover-image') as HTMLElement;
    
    if (mainImage && hoverImage) {
      mainImage.style.opacity = isHover ? '0' : '1';
      hoverImage.style.opacity = isHover ? '1' : '0';
    }
  };
  
  const handleAddToCart = async (product: any) => {

    // hasOptions가 true인 경우 상세페이지로 이동
    if (product.hasOptions) {
      navigate(`/products/${product.id}`);
      return;
    }
    
    // actions에서 제공하는 handleAddToCart 사용
    if (actions.handleAddToCart) {
      actions.handleAddToCart(product);
    } else {
      console.error('[BasicProductListSkinComplete] actions.handleAddToCart is not defined!');
    }
  };
  
  const handleLoadMore = () => {
    if (actions.handleLoadMore) {
      actions.handleLoadMore();
    }
  };
  
  const renderPagination = () => {

    // 페이지 수가 1개 이하면 페이지네이션 표시 안함
    if (totalPages <= 1) {
      return null;
    }
    
    const { handlePageChange } = actions;
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        gap: '5px',
        marginTop: '30px'
      }}>
        {/* 이전 페이지 버튼 */}
        <button
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            background: 'white',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            opacity: currentPage === 1 ? 0.5 : 1,
            borderRadius: '4px'
          }}
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {t('이전')}
        </button>
        
        {/* 페이지 번호들 */}
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }
          
          return (
            <button
              key={pageNum}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                background: currentPage === pageNum ? '#007bff' : 'white',
                color: currentPage === pageNum ? 'white' : 'black',
                cursor: 'pointer',
                borderRadius: '4px',
                fontWeight: currentPage === pageNum ? 'bold' : 'normal'
              }}
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </button>
          );
        })}
        
        {/* 다음 페이지 버튼 */}
        <button
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            background: 'white',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            opacity: currentPage === totalPages ? 0.5 : 1,
            borderRadius: '4px'
          }}
          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t('다음')}
        </button>
      </div>
    );
  };
  
  // 로딩 중 표시
  if (loading) {
    return (
      <div className="product-loading" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('상품 목록을 불러오는 중입니다...')}</p>
      </div>
    );
  }
  
  // 상품이 없는 경우
  if (!products.length) {
    return (
      <div className="no-products" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('상품이 없습니다.')}</p>
      </div>
    );
  }
  
  return (
    <section id={id} className="gi-shop product-list-component" style={{ paddingTop: isMobile ? '20px' : '40px' }}>
      <div className="container" style={{ paddingLeft: isMobile ? '15px' : '15px', paddingRight: isMobile ? '15px' : '15px' }}>
        <Row className={`${className} ${getItemsPerRowClass()}`} style={{ margin: '0' }}>
          <Col lg={lg} md={12} className={`gi-shop-rightside ${order}`} style={{ marginBottom: '30px' }}>
            <div className={`shop-pro-content ${isGridView ? "list-view-50" : ""}`}>
              <div className="shop-pro-inner">
                <Row>
                  {(isMobile ? mobileProducts : products)
                    .filter((_: any, index: number) => !excludeIndexes.includes(index.toString()) && !excludeIndexes.includes(index))
                    .map((product: any, index: number) => (
                    <Col 
                      key={product.id || index}
                      xl={isGridView ? 12 : xlValue}
                      lg={isGridView ? 12 : 3}
                      md={isGridView ? 12 : 6}
                      sm={isGridView ? 12 : 6}
                      xs={isGridView ? 12 : 6}
                      className={`${isGridView ? "col-md-6 col-xs-6 col-sm-6 width-50" : ""} gi-product-box pro-gl-content`}
                      style={{ paddingLeft: isMobile ? '5px' : '10px', paddingRight: isMobile ? '5px' : '10px', marginBottom: isMobile ? '10px' : '20px' }}
                    >
                      <div className="gi-product-content">
                        <div className={`gi-product-inner ${isGridView ? "d-flex" : ""}`}>
                          <div className={`gi-pro-image-outer ${isGridView ? "flex-shrink-0" : ""}`} 
                            style={isGridView ? {width: '30%'} as ExtendedCSSProperties : productImageStyle.imgContainer}>
                            <div className={`gi-pro-image ${product.image ? "product-hover-area" : ""}`} 
                              style={isGridView ? 
                                {width: "100%", height: "300px", display: "flex", alignItems: "center", justifyContent: "center"} as ExtendedCSSProperties : 
                                productImageStyle.imgWrapper}>
                              <a 
                                href={`/products/${product.id}`} 
                                className="image" 
                                style={productImageStyle.imageLink}
                                onMouseEnter={product.image ? (e) => handleImageHover(e, true) : undefined}
                                onMouseLeave={product.image ? (e) => handleImageHover(e, false) : undefined}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (actions.handleProductClick) {
                                    actions.handleProductClick(product);
                                  } else if (navigate) {
                                    navigate(`/products/${product.id}`);
                                  }
                                }}
                              >
                                {React.createElement(SafeImage as any, {
                                  src: product.image,
                                  alt: product.title,
                                  className: "main-image",
                                  style: product.image ? productImageStyle.mainImage : {},
                                  placeholderStyle: {
                                    position: "absolute" as CSSPosition,
                                    top: 0,
                                    left: 0
                                  }
                                })}
                                
                                {product.image && product.imageTwo && 
                                  React.createElement(SafeImage as any, {
                                    src: product.imageTwo,
                                    alt: product.title,
                                    className: "hover-image",
                                    style: productImageStyle.hoverImage
                                  })
                                }
                              </a>
                              {product.sale && (
                                <span className="flags">
                                  <span className="sale">{t(product.sale)}</span>
                                </span>
                              )}
                              
                              {product.image && (
                                <div className="product-action">
                                  <div className="pro-same-action pro-cart">
                                    <button
                                      title={product.hasOptions ? t('옵션 선택') : t('장바구니에 추가')}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleAddToCart(product);
                                      }}
                                    >
                                      <i className={product.hasOptions ? "fas fa-plus" : "fas fa-shopping-cart"}></i>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`gi-pro-content ${isGridView ? "flex-grow-1 ps-4" : ""}`}>
                            <h6 className="gi-pro-title">
                              <a 
                                href={`/products/${product.id}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (actions.handleProductClick) {
                                    actions.handleProductClick(product);
                                  } else if (navigate) {
                                    navigate(`/products/${product.id}`);
                                  }
                                }}
                              >
                                {t(product.title)}
                              </a>
                            </h6>
                            <div className="gi-pro-price">
                              {(() => {
                                // companyId가 22인 경우 로그인 상태에 따른 가격 표시
                                if (product.companyId === 22) {
                                  const defaultPrice = Number(product.config?.default_price || 0);
                                  const discountedPrice = Number(product.config?.discounted_price || 0);
                                  
                                  const isLoggedIn = Boolean(isUserLoggedIn);
                                  
                                  if (isLoggedIn) {
                                    if (defaultPrice > discountedPrice && discountedPrice > 0) {
                                      return React.createElement(DiscountRatePriceDisplay as any, {
                                        discountedPrice: discountedPrice,
                                        originalPrice: defaultPrice,
                                        discountedPriceClassName: "new-price",
                                        originalPriceClassName: "old-price"
                                      });
                                    } else {
                                      return React.createElement(PriceDisplay as any, {
                                        price: discountedPrice || defaultPrice,
                                        className: "new-price"
                                      });
                                    }
                                  } else {
                                    return React.createElement(PriceDisplay as any, {
                                      price: defaultPrice,
                                      className: "new-price"
                                    });
                                  }
                                } else {
                                  // companyId가 22가 아닌 경우 - 등급별 가격 체크
                                  if (product.hasLevelPrice && product.levelName) {
                                    // 등급별 가격이 있는 경우
                                    return (
                                      <div className="level-price-wrapper">
                                        {React.createElement(DiscountRatePriceDisplay as any, {
                                          discountedPrice: product.price,
                                          originalPrice: product.originalPrice,
                                          discountedPriceClassName: "new-price",
                                          originalPriceClassName: "old-price"
                                        })}
                                        <div className="level-name" style={{ fontSize: '11px', color: '#28a745', marginTop: '2px' }}>
                                          {product.levelName}
                                        </div>
                                      </div>
                                    );
                                  } else if (product.oldPrice > product.newPrice) {
                                    return React.createElement(DiscountRatePriceDisplay as any, {
                                      discountedPrice: product.newPrice,
                                      originalPrice: product.oldPrice,
                                      discountedPriceClassName: "new-price",
                                      originalPriceClassName: "old-price"
                                    });
                                  } else {
                                    return React.createElement(PriceDisplay as any, {
                                      price: product.newPrice,
                                      className: "new-price"
                                    });
                                  }
                                }
                              })()}
                            </div>
                            {isGridView && (
                              <div className="gi-pro-desc">
                                <p>{product.description || t('상품 설명이 없습니다.')}</p>
                              </div>
                            )}
                            {showStock && (
                              <div className="gi-pro-stock">
                                <span className={`stock-status ${product.stockCount !== undefined && product.stockCount <= 10 && product.stockCount > 0 ? "low-stock" : ""}`}>
                                  {!product.isInStock
                                    ? <span style={{color: "red"}}>{t('품절')}</span> 
                                    : `${t('재고')}: ${product.stockCount || 0}`}
                                </span>
                              </div>
                            )}
                            {showRewardPoints && calculateRewardPoints(product) && (
                              <div className="gi-pro-reward-points" style={{ fontSize: '13px', color: '#ff9800', marginTop: '5px' }}>
                                {rewardPointLabel}: {calculateRewardPoints(product)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
              
              {/* 페이지네이션 */}
              <div className="product-pagination">
                {isMobile ? (
                  <div style={{ 
                    textAlign: 'center', 
                    marginTop: '30px', 
                    marginBottom: '30px',
                    padding: '0 15px'
                  }}>
                    {(mobilePage < totalPages || totalPages === 0) && (
                      <button 
                        ref={loadMoreButtonRef}
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        className="btn btn-primary load-more-btn"
                        style={{
                          padding: '12px 40px',
                          fontSize: '16px',
                          fontWeight: '500',
                          borderRadius: '25px',
                          border: 'none',
                          color: 'white',
                          width: '100%',
                          maxWidth: '300px',
                          position: 'relative',
                          overflow: 'hidden',
                          backgroundColor: '#007bff'
                        }}
                      >
                        {isLoadingMore ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('로딩 중...')}
                          </>
                        ) : (
                          t('더보기')
                        )}
                      </button>
                    )}
                    {mobilePage >= totalPages && totalPages > 0 && (
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        {t('모든 상품을 보셨습니다.')}
                      </div>
                    )}
                  </div>
                ) : (
                  totalPages > 1 && renderPagination()
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default BasicProductListSkinComplete;